import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { routes } from '@platform/core/lib/routes';
import type { IRecordTypeDefinition } from '@platform/core/types';
import { useLabel } from '@platform/hooks/useLabel';
import { useGetCatalogQuery, useGetModuleDefinitionsQuery } from '@platform/state/api';
import type { IRelatedPill } from './IRelatedPill';

/** Скільки пов'язаних модулів показує діаграма (прототип v4: relM.slice(0, 6)). */
const MAX_PILLS = 6;

/**
 * Пігулки пов'язаних модулів діаграми моделі даних (прототип v4 relEls: RELATED_DEFS модуля, знайдені серед модулів простору, до шести;
 * напис — `rm.singular + ' · ' + rm.name.slice(0, 14)`, клік — модель даних того модуля): ключ пов'язаного — модуль каталогу простору
 * або тип записів модуля (прототип тримає дочірні таблиці окремими модулями: «Помічник · Помічники»); однина — з визначення простору.
 */
export function useRelatedPills(ws: string, main: IRecordTypeDefinition | undefined): IRelatedPill[] {
  const label = useLabel();
  const navigate = useNavigate();
  const { data: catalog } = useGetCatalogQuery({ workspaceId: ws }, { skip: !ws });
  const targets = useMemo(() => (main?.related ?? []).flatMap((r) => {
    const module = catalog?.modules.find((m) => m.key === r.module && m.installed);
    if (module) return [{ moduleKey: module.key, rtKey: (module.recordTypes.find((x) => x.main) ?? module.recordTypes[0])?.key, name: module.name }];
    const owner = catalog?.modules.find((m) => m.installed && m.recordTypes.some((x) => x.key === r.module));
    const rt = owner?.recordTypes.find((x) => x.key === r.module);
    return owner && rt ? [{ moduleKey: owner.key, rtKey: rt.key, name: rt.name }] : [];
  }).slice(0, MAX_PILLS), [main, catalog]);
  const keys = [...new Set(targets.map((x) => x.moduleKey))].sort().join(',');
  const { data: defs } = useGetModuleDefinitionsQuery({ keys, workspaceId: ws }, { skip: !keys });
  return targets.map((x, i) => {
    const rt = defs?.find((d) => d.module.key === x.moduleKey)?.recordTypes.find((r) => r.key === x.rtKey);
    const name = label(x.name);
    const singular = rt ? label(rt.singular ?? rt.name) : name;
    return { key: `${x.moduleKey}:${x.rtKey}:${i}`, label: `${singular} · ${name.slice(0, 14)}`, name, onOpen: () => navigate(routes.moduleDesign(x.moduleKey, 'model')) };
  });
}
